import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

// MUI
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

// Services
import productionPlanningService from '@services/productionPlanning';

// Common
import { Asterisk } from '@commons/Asterisk';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { GENERIC_MODAL_CLOSE_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { GenericCancelButton, GenericDownloadButton } from '@commons/Buttons';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { getUserTimezone } from '@commons/utils/date';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import { SingleDatePicker } from '@commons/DatePickers/SingleDatePicker';
import { ToggleSwitch, Tooltip } from '@commons/utils/styledLibraryComponents';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

// Hooks
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

// Design
import {
  Container,
  ContentContainer,
  FooterContainer,
  HeaderContainer,
  Section,
  Title,
  ToggleRow,
  TooltipIcon,
} from './styledComponents';

import { downloadFileFromBase64 } from '@admin/products/recipes/utils/downloadRecipe';

const MAX_PERIOD_DAYS = 7;
const MAX_TIME_SLOTS = 7;

/**
 * Check if the store has production time slots configured
 * A store with only the default time slot (name === null) is considered as having no time slots
 */
const hasProductionTimeSlots = (productionTimeSlots = []) => {
  const activeProductionTimeSlots =
    productionTimeSlots?.filter(({ name, active }) => name !== null && active) || [];

  return activeProductionTimeSlots.length > 0;
};

/**
 * @param {{
 *  categories: string[]
 *  onClose: () => void
 *  startDate: Date
 *  endDate: Date
 *  storeId: string
 *  selectedStore: object
 * }} props
 *
 * @return {JSX.Element}
 */
const PdfConfig = (props) => {
  const USER_TIMEZONE = getUserTimezone();
  const TODAY = moment.tz(USER_TIMEZONE);

  const {
    categories,
    endDate,
    startDate,
    storeId,
    selectedStore,
    onClose,
    // From redux
    showErrorMessage,
    pageLoading,
    pageLoaded,
    openGenericModal,
  } = props;

  const storeHasTimeSlots = hasProductionTimeSlots(selectedStore.productionTimeSlots);

  const [withTimeSlots, setWithTimeSlots] = useState(false);

  useEffect(() => {
    if (!storeHasTimeSlots) {
      setWithTimeSlots(false);
    }
  }, [storeHasTimeSlots]);

  const periodPickerState = usePeriodDatePickerState(startDate, endDate, USER_TIMEZONE);
  const [selectedDate, setSelectedDate] = useState(startDate || TODAY);

  const [includeProducts, setIncludeProducts] = useState(true);
  const [includeRecipes, setIncludeRecipes] = useState(false);
  const [includeIngredients, setIncludeIngredients] = useState(false);

  const handleToggleTimeSlots = () => {
    setWithTimeSlots(!withTimeSlots);
  };

  const getTooltipText = () => {
    if (!storeHasTimeSlots) {
      return i18next.t('FORECAST.SALES_PLANNING.PDF_TIME_SLOTS_TOOLTIP_DISABLED');
    }

    return i18next.t('FORECAST.SALES_PLANNING.PDF_TIME_SLOTS_TOOLTIP_DEFAULT');
  };

  const getPeriodTooltipText = () => {
    if (withTimeSlots) {
      return i18next.t('FORECAST.SALES_PLANNING.PDF_TIME_SLOTS_TOOLTIP_ENABLED');
    }

    return i18next.t('FORECAST.SALES_PLANNING.PDF_TIME_SLOTS_TOOLTIP_NO_TIME_SLOTS');
  };

  const getMaxFutureDate = () => {
    if (!periodPickerState.startDate) {
      return null;
    }

    return moment(periodPickerState.startDate).add(MAX_PERIOD_DAYS - 1, 'days');
  };

  const generateSalesPlanningPdf = async () => {
    // Check time slots limit when exporting with time slots
    if (withTimeSlots) {
      const activeTimeSlots = selectedStore.productionTimeSlots.filter(
        ({ active, name }) => active && name !== null,
      );

      if (activeTimeSlots.length > MAX_TIME_SLOTS) {
        const warningModalConfig = getConfirmationModal({
          type: 'warning',
          title: i18next.t('FORECAST.SALES_PLANNING.PDF_GENERATION_ERROR'),
          icon: '/images/inpulse/warning-white-small.svg',
          content: i18next.t('FORECAST.SALES_PLANNING.PDF_MAX_TIME_SLOTS_ERROR'),
          actions: [GENERIC_MODAL_CLOSE_BUTTON()],
        });

        openGenericModal(warningModalConfig);

        return;
      }
    }

    const exportStartDate = withTimeSlots
      ? moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
      : moment(periodPickerState.startDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

    const exportEndDate = withTimeSlots
      ? moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
      : moment(periodPickerState.endDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

    const payload = {
      categoryIds: categories.map(({ id }) => id),
      startDate: exportStartDate,
      endDate: exportEndDate,
      withTimeSlots,
      planningConfiguration: {
        includeProducts,
        includeRecipes,
        includeIngredients,
      },
    };

    pageLoading();

    try {
      const { fileName, rawBase64File } = await productionPlanningService.downloadSalesPlanningPDF(
        storeId,
        payload,
      );

      downloadFileFromBase64(rawBase64File, fileName);
    } catch {
      showErrorMessage(i18next.t('FORECAST.SALES_PLANNING.PDF_GENERATION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const resetStateAndClose = () => {
    setWithTimeSlots(false);
    setIncludeProducts(true);
    setIncludeRecipes(false);
    setIncludeIngredients(false);
    onClose();
  };

  const isDownloadDisabled =
    (!includeProducts && !includeRecipes && !includeIngredients) || // If none of the data type options is selected
    (!withTimeSlots && (!periodPickerState.startDate || !periodPickerState.endDate)) || // If the period is not properly set in the absence of time slots
    (withTimeSlots && !selectedDate); // If no date is selected in the presence of time slots

  return (
    <Container>
      <HeaderContainer>
        <Title>{i18next.t('FORECAST.SALES_PLANNING.PDF_CONFIG_TITLE')}</Title>
      </HeaderContainer>

      <ContentContainer>
        <Section>
          <ToggleRow>
            <ToggleSwitch
              checked={withTimeSlots}
              disabled={!storeHasTimeSlots}
              handleClick={handleToggleTimeSlots}
              id="time-slots-toggle"
            />
            <Text color={ENUM_COLORS.IP_BLACK_1} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
              {i18next.t('FORECAST.SALES_PLANNING.PDF_TIME_SLOTS_TOGGLE')}
            </Text>
            <Tooltip
              renderTooltipTrigger={() => (
                <TooltipIcon src="/images/inpulse/info-black-small.svg" />
              )}
              text={getTooltipText()}
            />
          </ToggleRow>
        </Section>

        <Section>
          <ToggleRow>
            <Text color={ENUM_COLORS.IP_BLACK_1}>
              <Asterisk />
              {i18next.t('FORECAST.SALES_PLANNING.PDF_PRODUCTION_PERIOD')} :
            </Text>
            <Tooltip
              renderTooltipTrigger={() => (
                <TooltipIcon src="/images/inpulse/info-black-small.svg" />
              )}
              text={getPeriodTooltipText()}
            />
          </ToggleRow>
          {withTimeSlots ? (
            <SingleDatePicker
              customStyle={{ position: 'inherit' }}
              date={selectedDate}
              maxPastDate={TODAY}
              onDateChange={setSelectedDate}
            />
          ) : (
            <PeriodDatePicker
              endDate={periodPickerState.endDate}
              focusedDateInput={periodPickerState.focusedDateInput}
              maxFutureDate={getMaxFutureDate()}
              maxPastDate={TODAY}
              minimumNights={0}
              setFocusedDateInput={periodPickerState.setFocusedDateInput}
              startDate={periodPickerState.startDate}
              timezone={USER_TIMEZONE}
              onDatesChange={periodPickerState.handleSelectedDates}
            />
          )}
        </Section>

        <Section>
          <Text color={ENUM_COLORS.IP_BLACK_1}>
            <Asterisk />
            {i18next.t('FORECAST.SALES_PLANNING.PDF_TYPE')} :
          </Text>
          <FormGroup sx={{ paddingLeft: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeProducts}
                  size="large"
                  onChange={(e) => setIncludeProducts(e.target.checked)}
                />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_PRODUCTS')}
            />
          </FormGroup>

          <FormGroup sx={{ paddingLeft: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeRecipes}
                  size="large"
                  onChange={(e) => setIncludeRecipes(e.target.checked)}
                />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_RECIPES')}
            />
          </FormGroup>

          <FormGroup sx={{ paddingLeft: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeIngredients}
                  size="large"
                  onChange={(e) => setIncludeIngredients(e.target.checked)}
                />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_INGREDIENTS')}
            />
          </FormGroup>
        </Section>
      </ContentContainer>

      <FooterContainer>
        <GenericCancelButton handleClick={resetStateAndClose} />
        <GenericDownloadButton
          handleClick={generateSalesPlanningPdf}
          isDisabled={isDownloadDisabled}
        />
      </FooterContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PdfConfig);
